import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { driversForShift } from './mockRoster';
import { slotsForDriver } from './mockAssignments';
import { SEED_TEAMS } from './mockTeams';
import { RUNS_BY_ID } from './mockBank';
import {
  ZOOM_LEVELS, DEFAULT_ZOOM, widthForZoom,
  pxFromMins, hourTicks, halfHourTicks,
} from './timeScale';
import { C } from './styles';
import { RunQueueProvider, useRunQueue, DND } from './runqueue/RunQueueContext';
import RunQueueDrawer from './runqueue/RunQueueDrawer';
import { SearchInput, rowMatchesQuery } from './searchBar';

const ROW_H        = 44;
const HEADER_H     = 40;
const SIDEBAR_W    = 220;       // # / Name / Shift sidebar (content)
const PUCK_DURATION_DEFAULT = 55; // mins, like the legacy default

function hexToRgba(hex, alpha) {
  if (!hex) return null;
  const m = hex.replace('#', '').match(/.{2}/g);
  if (!m || m.length < 3) return null;
  const [r, g, b] = m.map(x => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function TimelineView() {
  return (
    <RunQueueProvider>
      <TimelineViewInner />
    </RunQueueProvider>
  );
}

function TimelineViewInner() {
  const [shift, setShift] = useState('AM');
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    assignments, snap, assignRunToDriver, moveSlot,
    dragPreview, dragCandidates,
    drawerSide,
  } = useRunQueue();

  // Look up the dragged run's ETA so each candidate lane can show a
  // ghost puck at exactly the right place on the timeline.
  const dragRunEtaMins = useMemo(() => {
    if (!dragPreview) return null;
    const run = RUNS_BY_ID.get(dragPreview.runId);
    return run ? run.inboundEtaMins : null;
  }, [dragPreview]);

  // Driver-team color map (same teams as the assignment grid for now).
  const driverTeamColor = useMemo(() => {
    const map = new Map();
    SEED_TEAMS.forEach(t => t.driverIds.forEach(id => map.set(id, t.color)));
    return map;
  }, []);

  const drivers = useMemo(() => driversForShift(shift), [shift]);

  // Order: team members clustered (by color), then everyone else.
  const orderedDrivers = useMemo(() => {
    const inTeam = [], noTeam = [];
    drivers.forEach(d => (driverTeamColor.has(d.id) ? inTeam : noTeam).push(d));
    inTeam.sort((a, b) => {
      const ca = driverTeamColor.get(a.id);
      const cb = driverTeamColor.get(b.id);
      return ca === cb ? 0 : (ca < cb ? -1 : 1);
    });
    return [...inTeam, ...noTeam];
  }, [drivers, driverTeamColor]);

  // Pre-compute pucks per driver (only those with assignments).
  const driverRows = useMemo(() => orderedDrivers.map((driver) => {
    const slots = slotsForDriver(driver.id, assignments);
    const pucks = slots
      .map((run, slotIdx) => run ? {
        runId: run.id,
        slotIdx,
        flightNumber: run.inboundFlightNumber,
        from: run.inboundFrom,
        gate: run.inboundGate,
        eta: run.inboundEta,
        etaMins: run.inboundEtaMins,
        slotLabel: run.slotLabel,
        category: run.hasHot ? 'hot'
                : run.hasPriority ? 'priority'
                : run.hasCold ? 'cold'
                : 'normal',
      } : null)
      .filter(Boolean);
    const candidateFit = dragCandidates ? dragCandidates.get(driver.id) || null : null;
    return {
      driver, pucks, slots,
      teamColor: driverTeamColor.get(driver.id) || null,
      candidateFit,
    };
  }), [orderedDrivers, driverTeamColor, assignments, dragCandidates]);

  // Filter lanes by search query. Same matcher as the grid so a typed
  // query behaves the same in both views.
  const filteredRows = useMemo(
    () => driverRows.filter(({ driver, slots }) => rowMatchesQuery(searchQuery, driver, slots)),
    [driverRows, searchQuery]
  );

  // Stats for the toolbar.
  const stats = useMemo(() => {
    const totalDrivers = drivers.length;
    const assignedCount = Array.from(assignments.values()).reduce((n, l) => n + l.length, 0);
    return { totalDrivers, assignedCount };
  }, [drivers, assignments]);

  const pxPerMin = ZOOM_LEVELS[zoomIdx].pxPerMin;
  const lanesW   = widthForZoom(zoomIdx);
  const totalW   = SIDEBAR_W + lanesW;

  // Read the actual rendered offsetLeft of the header's lane-labels box and
  // mirror it for GridOverlay. This bypasses any box-model assumptions
  // (border-box vs content-box, sticky positioning quirks, etc) that have
  // been the source of multiple alignment bugs.
  const laneRef = useRef(null);
  const [laneOriginX, setLaneOriginX] = useState(SIDEBAR_W);
  useLayoutEffect(() => {
    if (laneRef.current) setLaneOriginX(laneRef.current.offsetLeft);
  }, [zoomIdx, lanesW]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>

      {/* Toolbar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        px: 2, py: 1.25,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.surfaceSolid,
        flexShrink: 0, flexWrap: 'wrap',
      }}>
        <ToggleButtonGroup
          value={shift}
          exclusive
          onChange={(_, v) => v && setShift(v)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: C.textSecondary, borderColor: C.border,
              fontSize: '0.7rem', fontWeight: 700, px: 1.5, py: 0.4,
              textTransform: 'none', letterSpacing: 0.4,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: C.accentBg, color: C.accent, borderColor: C.accent,
              '&:hover': { backgroundColor: 'rgba(249, 182, 18, 0.2)' },
            },
          }}
        >
          <ToggleButton value="AM">AM</ToggleButton>
          <ToggleButton value="MID">MID</ToggleButton>
          <ToggleButton value="PM">PM</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StatChip label="Drivers" value={stats.totalDrivers} />
          <StatChip label="Pucks"   value={stats.assignedCount} color={C.ok} />
        </Box>

        {/* Search — mirrors the AssignmentView search behavior so a
            dispatcher's muscle memory works in both views. */}
        <SearchInput value={searchQuery} onChange={setSearchQuery} />

        <Box sx={{ flex: 1 }} />

        {/* Zoom */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, mr: 0.5 }}>
            Zoom
          </Typography>
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
                disabled={zoomIdx === 0}
                sx={{ color: C.textSecondary, '&:hover': { color: C.accent }, '&.Mui-disabled': { color: C.textMuted } }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography sx={{ fontSize: '0.7rem', color: C.textPrimary, fontWeight: 700, minWidth: 32, textAlign: 'center' }}>
            {ZOOM_LEVELS[zoomIdx].label}
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
                disabled={zoomIdx === ZOOM_LEVELS.length - 1}
                sx={{ color: C.textSecondary, '&:hover': { color: C.accent }, '&.Mui-disabled': { color: C.textMuted } }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Run
          </Typography>
          <LegendDot color={C.hot}    label="Hot" />
          <LegendDot color={C.warn}   label="Pri" />
          <LegendDot color={C.cold}   label="Cold" />
        </Box>
      </Box>

      {/* Drawer + scroll region — drawer docks left or right per
          dispatcher preference (shared with AssignmentView). */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
        {drawerSide === 'left' && <RunQueueDrawer />}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        position: 'relative',
        backgroundColor: C.surfaceSolid,
        '&::-webkit-scrollbar': { height: 8, width: 8 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 4,
        },
      }}>
        <Box sx={{ width: totalW, minWidth: '100%', position: 'relative' }}>

          {/* Header row (sticky top) */}
          <Box sx={{
            display: 'flex',
            position: 'sticky', top: 0, zIndex: 5,
            height: HEADER_H,
            backgroundColor: C.surfaceHiSolid,
            borderBottom: `2px solid ${C.borderStrong}`,
          }}>
            {/* Sidebar header */}
            <Box sx={{
              width: SIDEBAR_W, flexShrink: 0,
              position: 'sticky', left: 0, zIndex: 6,
              backgroundColor: C.surfaceHiSolid,
              borderRight: `2px solid ${C.borderStrong}`,
              display: 'flex', alignItems: 'center', px: 1.25,
              fontSize: '0.6rem', color: C.textMuted, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              Agent
            </Box>
            {/* Hour labels */}
            <Box ref={laneRef} sx={{ position: 'relative', width: lanesW, height: '100%' }}>
              {hourTicks().map(({ minute, label }) => {
                const left = pxFromMins(minute, pxPerMin);
                return (
                  <Box key={minute} sx={{
                    position: 'absolute', left, top: 0, bottom: 0,
                    display: 'flex', alignItems: 'center',
                  }}>
                    <Box sx={{
                      width: '1px', height: '60%',
                      backgroundColor: C.borderStrong,
                      mr: 0.5,
                    }} />
                    <Typography sx={{
                      fontSize: '0.65rem', fontWeight: 700, color: C.textSecondary,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Body */}
          {filteredRows.map(({ driver, pucks, teamColor, candidateFit }, rowIdx) => (
            <DriverLane
              key={driver.id}
              driver={driver}
              pucks={pucks}
              teamColor={teamColor}
              rowIdx={rowIdx}
              pxPerMin={pxPerMin}
              lanesW={lanesW}
              selectedFlight={selectedFlight}
              onSelectFlight={setSelectedFlight}
              snap={snap}
              onAssign={assignRunToDriver}
              onMove={moveSlot}
              candidateFit={candidateFit}
              candidateGhostMins={candidateFit ? dragRunEtaMins : null}
            />
          ))}

          {/* Vertical gridlines layered over the body — absolute so they
              span the full height. Pointer-events off so pucks stay clickable.
              `left` is the actually-measured offsetLeft of the header lane
              labels box, so gridlines line up with whatever the browser
              decided the lane origin is. */}
          <GridOverlay
            pxPerMin={pxPerMin}
            lanesW={lanesW}
            bodyTop={HEADER_H}
            laneLeft={laneOriginX}
          />
        </Box>
        </Box>
        {drawerSide === 'right' && <RunQueueDrawer />}
      </Box>
    </Box>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

const DriverLane = React.memo(function DriverLane({
  driver, pucks, teamColor, rowIdx, pxPerMin, lanesW, selectedFlight, onSelectFlight,
  snap, onAssign, onMove,
  candidateFit, candidateGhostMins,
}) {
  const [dragOver, setDragOver] = useState(false);
  // Team rows still get a subtle tinted wash so teaming reads in the
  // timeline view too. Non-team rows are fully transparent.
  const baseBg  = teamColor ? hexToRgba(teamColor, 0.10) : 'transparent';
  const hoverBg = teamColor ? hexToRgba(teamColor, 0.20) : 'rgba(255, 255, 255, 0.05)';

  const handleDragOver = (e) => {
    const types = e.dataTransfer.types;
    if (types.includes(DND.RUN_FROM_QUEUE) || types.includes(DND.RUN_FROM_GRID)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!dragOver) setDragOver(true);
    }
  };
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const queueRunId = e.dataTransfer.getData(DND.RUN_FROM_QUEUE);
    if (queueRunId) { onAssign(queueRunId, driver.id); return; }
    const gridPayload = e.dataTransfer.getData(DND.RUN_FROM_GRID);
    if (gridPayload) {
      const [fromDriverId, fromSlotStr] = gridPayload.split('|');
      onMove(fromDriverId, parseInt(fromSlotStr, 10), driver.id);
    }
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
      display: 'flex',
      height: ROW_H,
      borderBottom: `1px solid ${C.border}`,
      backgroundColor: dragOver ? `${C.accentBg} !important` : baseBg,
      boxShadow: dragOver ? `inset 0 0 0 2px ${C.accent}` : 'none',
      transition: 'background-color 80ms, box-shadow 80ms',
      '&:hover': {
        backgroundColor: hoverBg,
        // Sticky sidebar gets a solid hover (not the translucent row
        // tint) so pucks scrolling under it can't bleed through.
        '& .lane-sidebar': { backgroundColor: C.surfaceHiSolid },
      },
    }}>
      {/* Sticky agent sidebar — solid bg so the pucks scrolling under
          it can't bleed through the name. Team identity is preserved
          via the 4px absolute-positioned color bar on the left. */}
      <Box className="lane-sidebar" sx={{
        width: SIDEBAR_W, flexShrink: 0,
        position: 'sticky', left: 0, zIndex: 3,
        backgroundColor: C.surfaceSolid,
        borderRight: `2px solid ${C.borderStrong}`,
        display: 'flex', alignItems: 'center', gap: 1,
        pl: '12px', pr: 1.25,
        transition: 'background-color 60ms',
      }}>
        {teamColor && (
          <Box sx={{
            position: 'absolute',
            left: 0, top: 0, bottom: 0,
            width: '4px',
            backgroundColor: teamColor,
          }} />
        )}
        <Typography sx={{
          fontSize: '0.65rem', color: C.textMuted, fontWeight: 700,
          minWidth: 22, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
        }}>
          {rowIdx + 1}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.78rem', color: C.textPrimary, fontWeight: 600 }}>
            {driver.displayName}
            {' '}
            <Box component="span" sx={{ color: C.textMuted, fontWeight: 400, fontSize: '0.7rem' }}>
              ({driver.seniority})
            </Box>
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, lineHeight: 1 }}>
            {driver.shiftCode}
          </Typography>
        </Box>
      </Box>

      {/* Lane */}
      <Box sx={{ position: 'relative', width: lanesW, height: '100%' }}>
        {/* Candidate ghost puck — shows where the dragged run would land
            for this driver. Rendered under the real pucks so existing
            assignments stay readable. */}
        {candidateFit && candidateGhostMins != null && (
          <CandidateGhostPuck
            left={pxFromMins(candidateGhostMins, pxPerMin)}
            width={Math.max(40, PUCK_DURATION_DEFAULT * pxPerMin)}
            fit={candidateFit}
          />
        )}
        {pucks.map((p) => {
          const left  = pxFromMins(p.etaMins, pxPerMin);
          const width = Math.max(40, PUCK_DURATION_DEFAULT * pxPerMin);
          const isSelected = selectedFlight && selectedFlight === p.flightNumber;
          const dimmed = selectedFlight && !isSelected;
          const justSnapped = snap && snap.driverId === driver.id && snap.slot === p.slotIdx;
          return (
            <Puck
              key={p.runId}
              left={left}
              width={width}
              puck={p}
              driverId={driver.id}
              snapped={justSnapped}
              selected={isSelected}
              dimmed={dimmed}
              onClick={() => onSelectFlight(isSelected ? null : p.flightNumber)}
            />
          );
        })}
      </Box>
    </Box>
  );
});

// Ghost puck shown while the dispatcher is dragging a queue tile —
// previews where the run would land in this driver's timeline. Color
// matches the candidate fit: green/team for good, red for "can't take"
// (shift mismatch, full, or red gap). Pointer-events disabled so it
// doesn't intercept the drop.
function CandidateGhostPuck({ left, width, fit }) {
  const variant = fit === 'team'
    ? { bg: 'rgba(76, 175, 80, 0.35)',  border: 'rgba(76, 175, 80, 0.9)' }
    : fit === 'red'
    ? { bg: 'rgba(255, 0, 0, 0.22)',    border: 'rgba(255, 0, 0, 0.8)' }
    : { bg: 'rgba(76, 175, 80, 0.22)',  border: 'rgba(76, 175, 80, 0.7)' };
  return (
    <Box sx={{
      position: 'absolute',
      top: 4, height: ROW_H - 12,
      left, width,
      backgroundColor: variant.bg,
      border: `2px dashed ${variant.border}`,
      borderRadius: 1,
      pointerEvents: 'none',
      zIndex: 1,
      animation: 'ghostPuckPulse 1.4s ease-in-out infinite',
      '@keyframes ghostPuckPulse': {
        '0%, 100%': { opacity: 0.7 },
        '50%':      { opacity: 1 },
      },
    }} />
  );
}

function Puck({ left, width, puck, driverId, snapped, selected, dimmed, onClick }) {
  const accent = puck.category === 'hot'      ? C.hot
              : puck.category === 'priority'  ? C.warn
              : puck.category === 'cold'      ? C.cold
              : 'rgba(255, 255, 255, 0.35)';
  const num = (puck.flightNumber || '').replace(/^WN\s*/, '');

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND.RUN_FROM_GRID, `${driverId}|${puck.slotIdx}`);
    e.dataTransfer.setData('text/plain', `${driverId}|${puck.slotIdx}`);
  };

  return (
    <Box
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      sx={{
        position: 'absolute',
        top: 4, height: ROW_H - 12,
        left, width,
        backgroundColor: 'rgba(20, 20, 25, 0.78)',
        border: `1px solid ${selected ? C.accent : C.borderStrong}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 1,
        boxShadow: selected
          ? `0 0 0 1px ${C.accent}, 0 4px 12px rgba(0, 0, 0, 0.5)`
          : '0 1px 3px rgba(0, 0, 0, 0.4)',
        opacity: dimmed ? 0.35 : 1,
        cursor: 'grab',
        display: 'flex', alignItems: 'center',
        gap: 0.75, px: 0.75,
        overflow: 'hidden',
        transition: 'opacity 90ms, transform 90ms, box-shadow 90ms',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.55)',
          zIndex: 2,
        },
        '&:active': { cursor: 'grabbing' },
        ...(snapped && {
          animation: 'puckSnapIn 600ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          '@keyframes puckSnapIn': {
            '0%':   { transform: 'scale(1.15)', boxShadow: `0 0 0 3px ${C.accent}, 0 8px 18px rgba(249, 182, 18, 0.6)` },
            '60%':  { transform: 'scale(1)',    boxShadow: `0 0 0 2px ${C.accent}, 0 4px 12px rgba(249, 182, 18, 0.35)` },
            '100%': { transform: 'scale(1)',    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)' },
          },
        }),
      }}
    >
      <Typography sx={{
        fontSize: '0.72rem', fontWeight: 700, color: C.textPrimary,
        fontVariantNumeric: 'tabular-nums', lineHeight: 1,
      }}>
        {num}
      </Typography>
      <Typography sx={{
        fontSize: '0.6rem', color: C.textMuted, lineHeight: 1,
      }}>
        {puck.from}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography sx={{
        fontSize: '0.62rem', color: C.textSecondary, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums', lineHeight: 1,
      }}>
        {puck.eta}
      </Typography>
    </Box>
  );
}

function GridOverlay({ pxPerMin, lanesW, bodyTop, laneLeft }) {
  const halves = halfHourTicks();
  const hours  = hourTicks();
  return (
    <Box sx={{
      position: 'absolute',
      top: bodyTop, bottom: 0,
      // Mirror the header lane-labels box's actual offsetLeft so gridlines
      // align with whatever x the browser chose, regardless of box-sizing.
      left: laneLeft,
      width: lanesW,
      pointerEvents: 'none',
      zIndex: 1,
    }}>
      {hours.map(({ minute }) => (
        <Box key={`h-${minute}`} sx={{
          position: 'absolute', top: 0, bottom: 0,
          left: pxFromMins(minute, pxPerMin),
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        }} />
      ))}
      {halves.map(m => (
        <Box key={`hh-${m}`} sx={{
          position: 'absolute', top: 0, bottom: 0,
          left: pxFromMins(m, pxPerMin),
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
        }} />
      ))}
    </Box>
  );
}

function StatChip({ label, value, color = C.textPrimary }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 0.4,
      px: 1, py: 0.3,
      backgroundColor: C.surfaceAlt,
      border: `1px solid ${C.border}`,
      borderRadius: 1,
    }}>
      <Typography sx={{ fontSize: '0.85rem', color, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.55rem', color: C.textMuted }}>{label}</Typography>
    </Box>
  );
}

export default TimelineView;
